import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, catchError, map, of, shareReplay } from "rxjs";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { AuthService } from "src/app/services/auth.service";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

export function initialsOf(name: string): string {
  const parts = (name ?? "").split(/[\s.@_-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : (name ?? "").slice(0, 2);

  return letters.toUpperCase();
}

@Component({
    selector: "app-user-avatar",
    imports: [CommonModule, SvgIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div
      class="user-avatar"
      [style.width.px]="size"
      [style.height.px]="size"
      [style.background-color]="backgroundColor"
    >
      <span *ngIf="!profilePictureUrl && initials" class="avatar-initials">{{ initials }}</span>
      <app-svg-icon
        *ngIf="!profilePictureUrl && !initials"
        icon="user"
        class="avatar-icon"
        [size]="size * 0.6"
        [strokeWidth]="1.5"
        color="var(--color-white)"
      ></app-svg-icon>
      <img
        class="avatar-image"
        *ngIf="profilePictureUrl"
        [src]="profilePictureUrl"
        [alt]="userEmail"
        [style.width.px]="size"
        [style.height.px]="size"
        (error)="handleImageError()"
      />
    </div>
  `,
    styles: [
        `
      .user-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        overflow: hidden;
        color: white;
        flex-shrink: 0;
      }

      .avatar-icon {
        line-height: 1;
      }

      .avatar-initials {
        font-size: 0.4em;
        font-weight: 600;
        letter-spacing: 0.02em;
        line-height: 1;
        user-select: none;
      }

      .avatar-image {
        border-radius: 50%;
        object-fit: cover;
        display: block;
      }
    `,
    ]
})
export class UserAvatarComponent implements OnInit {
  private static readonly photoCache = new Map<string, Observable<string | null>>();

  @Input() userEmail: string = "";
  @Input() size: number = 32;

  /** Shown in place of the generic person icon while no profile picture has loaded. */
  @Input() initials: string = "";

  profilePictureUrl: SafeUrl | null = null;
  backgroundColor: string = "var(--color-background-secondary)";

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.userEmail) {
      this.backgroundColor = this.generateColorFromEmail(this.userEmail);
      this.getUserProfilePicture();
    } else {
      this.backgroundColor = "var(--color-background-secondary)";
    }
  }

  handleImageError() {
    // A blob URL that will not render is no use to the next avatar for this person either.
    UserAvatarComponent.photoCache.delete(this.userEmail.toLowerCase());

    this.profilePictureUrl = null;
    this.cdr.markForCheck();
  }

  private getUserProfilePicture() {
    if (!this.userEmail) return;

    this.photoUrl(this.userEmail).subscribe((url) => {
      this.profilePictureUrl = url ? this.sanitizer.bypassSecurityTrustUrl(url) : null;
      this.cdr.markForCheck();
    });
  }

  /**
   * One Graph request per address for the life of the tab, shared by every avatar showing that
   * person — a list rendering an avatar per row would otherwise fire a request per row and be
   * throttled. The object URL is kept rather than revoked for the same reason: it is created once
   * per person, not once per render.
   */
  private photoUrl(userEmail: string): Observable<string | null> {
    const key = userEmail.toLowerCase();
    const cached = UserAvatarComponent.photoCache.get(key);

    if (cached) return cached;

    const endpoint = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(
      userEmail
    )}/photo/$value`;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.$graphToken.getValue()}`,
    });

    const request = this.http
      .get(endpoint, { headers, responseType: "blob" })
      .pipe(
        map((blob: Blob) => URL.createObjectURL(blob)),
        catchError((error) => {
          console.debug("Failed to load profile picture for", userEmail, error);

          return of(null);
        }),
        shareReplay(1)
      );

    UserAvatarComponent.photoCache.set(key, request);

    return request;
  }

  private generateColorFromEmail(email: string): string {
    if (!email) return "var(--color-background-secondary)";

    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }

    const h = hash % 360;
    return `hsl(${h}, 50%, 60%)`;
  }
}
